import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import './StockAssessmentReport.css';

interface StockAssessmentReportData {
  stockSlug?: string | null;
  selectedModel?: string;
}

const PDF_URLS: Record<string, string> = {
  redfish: '/2025_RED_UNIT_RPT_2025_09_17_121440.522737.pdf',
};

const TITLES: Record<string, string> = {
  redfish: 'Acadian Redfish Stock Assessment Report',
};

export default function StockAssessmentReport(
  props: RegistryComponentProps
): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<StockAssessmentReportData>(props);
  const [showModal, setShowModal] = useState(false);

  const pageStockSlug = pageData?.data?.stockSlug as string | undefined;
  const isWhamCompatibleStock = pageData?.data?.isWhamCompatibleStock as boolean | undefined;

  const stockSlug = data.stockSlug ?? pageStockSlug ?? null;

  const canShow = Boolean(isWhamCompatibleStock) && stockSlug === 'redfish';

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openStockAssessmentReportModal', handler);
    return () => window.removeEventListener('openStockAssessmentReportModal', handler);
  }, []);

  if (!canShow) return <></>;

  const pdfUrl = stockSlug ? PDF_URLS[stockSlug] : '';
  const title = stockSlug ? TITLES[stockSlug] : 'Stock Assessment Report';

  return (
    <div className="stock-assessment-report insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Stock Assessment Report</h3>
      </div>
      <div className="insight-columns">
        <div
          className="insight-column clickable-column"
          style={{ marginRight: 0, cursor: 'pointer' }}
          onClick={() => setShowModal(true)}
        >
          <div className="assessment-metric-item">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="column-header" style={{ textAlign: 'left', marginBottom: 0 }}>
                Overfished
              </span>
              <span className="insight-value" style={{ textAlign: 'right' }}>
                No
              </span>
            </div>
          </div>
          <div className="assessment-metric-item">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="column-header" style={{ textAlign: 'left', marginBottom: 0 }}>
                Overfishing
              </span>
              <span className="insight-value" style={{ textAlign: 'right' }}>
                No
              </span>
            </div>
          </div>
        </div>
      </div>

      {showModal &&
        pdfUrl &&
        createPortal(
          <Modal
            title={title}
            onClose={handleCloseModal}
            size="extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '95vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '0', height: '100%', overflow: 'hidden' }}
          >
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                minHeight: '80vh',
                border: 'none',
              }}
              title={title}
            />
          </Modal>,
          document.body
        )}
    </div>
  );
}
